import { cx, tv, VariantProps } from "@utils/tw"
import { animate } from "motion/react"
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react"

type TabControlItem = {
  label: ReactNode
  value: string
}

export type TabControlProps = {
  items: TabControlItem[]
  value: string
  onValueChange: (value: string) => void
  className?: string
  variant?: VariantProps<typeof createStyles>["variant"]
}

export function TabControl(props: TabControlProps) {
  const id = useId()
  const styles = createStyles({ variant: props.variant })

  const innerRef = useRef<HTMLDivElement>(null!)
  const indicatorRef = useRef<HTMLDivElement>(null!)
  const activeRef = useRef<HTMLLabelElement | null>(null)
  const [visiblyActiveValue, setVisiblyActiveValue] = useState<string | null>(null)

  const measure = useCallback(() => {
    const innerBounds = innerRef.current?.getBoundingClientRect()
    const activeBounds = activeRef.current?.getBoundingClientRect()
    if (activeBounds && innerBounds) {
      return {
        left: activeBounds.left - innerBounds.left,
        width: activeBounds.width,
      }
    }
  }, [])

  if (env.client) {
    useLayoutEffect(() => {
      const values = measure()
      if (values) {
        setVisiblyActiveValue(props.value)
        animate(
          indicatorRef.current,
          {
            x: values.left,
            width: values.width,
            opacity: 1,
          },
          {
            type: "spring",
            stiffness: 300,
            damping: 30,
          },
        )
      }
    }, [props.value])
  }

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      const values = measure()
      if (values) {
        animate(
          indicatorRef.current,
          {
            x: values.left,
            width: values.width,
            opacity: 1,
          },
          {
            duration: 0,
          },
        )
      }
    })
    ro.observe(innerRef.current!)
    return () => {
      ro.disconnect()
    }
  }, [])

  return (
    <div
      className={styles.base({
        className: props.className,
      })}
    >
      <div className={styles.inner()} ref={innerRef}>
        {props.items.map((item) => (
          <label
            ref={item.value === props.value ? activeRef : null}
            key={item.value}
            className={styles.tab({ active: item.value === visiblyActiveValue })}
          >
            <input
              type="radio"
              name={id}
              value={item.value}
              checked={item.value === props.value}
              className="appearance-none absolute opacity-0"
              onChange={(e) => {
                if (e.currentTarget.checked) props.onValueChange(item.value)
              }}
            />
            <span className={styles.label()}>{item.label}</span>
          </label>
        ))}
        <div ref={indicatorRef} className={cx(styles.indicator(), "opacity-0")}></div>
      </div>
    </div>
  )
}

const createStyles = tv({
  base: [
    "type-ui-m flex bg-bg-low group z-1",
    "p-[calc(var(--border-radius-lg)-var(--border-radius-md))] rounded-lg h-9",
    "md:p-[calc(var(--border-radius-md)-var(--border-radius-sm))] md:rounded-md md:h-control",
  ],
  slots: {
    inner: "flex gap-2 md:gap-1 relative",
    tab: [
      "bg-transparent px-3 lg:px-2 hover:bg-fg/5 relative inline-flex items-center cursor-pointer transition-colors",
      "rounded-md",
      "md:rounded-sm",
    ],
    indicator: ["absolute top-0 bg-fg z-0 h-full will-change-transform", "rounded-md", "md:rounded-sm"],
    label: "block relative z-[1]",
  },
  variants: {
    active: {
      true: {
        tab: "text-bg !bg-transparent",
      },
    },
    variant: {
      default: {},
      brand: {
        base: ["!h-controlOuter !p-3 md:!p-2 !rounded-tabLarge"],
        inner: ["!h-control"],
        indicator: ["!rounded-lgControl"],
        tab: ["!rounded-lgControl"],
        label: ["type-label-caps-m md:type-ui-hero px-4 text-center"],
      },
      subtle: {
        base: "p-0 h-auto bg-transparent",
        tab: "!rounded-[0.5em] h-[1.6em] px-[0.7em]",
      },
    },
  },
})
